/**
 * Encode an offscreen canvas to a thumbnail data URL with WebP-first,
 * JPEG-fallback, and an iterative-quality file-size cap.
 *
 * Why WebP first: at the same visual quality WebP is typically 25-35%
 * smaller than JPEG for photographic content, so we can ship sharper
 * thumbnails under the same byte budget.
 *
 * The cap (default 500 KB) is enforced by stepping down through a
 * quality ladder. If even the lowest quality cannot fit, we still
 * return that smallest variant so the upstream save flow never breaks
 * — we never produce a blocking failure for a thumbnail.
 */

const _WEBP_QUALITIES = [0.92, 0.85, 0.78, 0.7, 0.6];
const _JPEG_QUALITIES = [0.9, 0.82, 0.74, 0.66, 0.55];

function _approxBytes(dataUrl) {
  // base64 → ~0.75 bytes per char. Strip "data:image/...;base64," prefix.
  const i = dataUrl.indexOf(',');
  const b64Len = i >= 0 ? dataUrl.length - i - 1 : dataUrl.length;
  return Math.round(b64Len * 0.75);
}

function _supportsWebp() {
  try {
    const c = document.createElement('canvas');
    c.width = 1; c.height = 1;
    return c.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  } catch { return false; }
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {{ maxBytes?: number, preferJpeg?: boolean, webpSupported?: boolean }} [opts]
 * @returns {{ dataUrl: string, mime: string, quality: number, bytes: number }}
 */
export function encodeThumbnail(canvas, opts = {}) {
  const maxBytes = opts.maxBytes ?? 500 * 1024;
  const webpSupported = opts.webpSupported ?? _supportsWebp();

  const tryLadder = (mime, ladder) => {
    let best = null;
    for (const q of ladder) {
      const dataUrl = canvas.toDataURL(mime, q);
      const bytes = _approxBytes(dataUrl);
      best = { dataUrl, mime, quality: q, bytes };
      if (bytes <= maxBytes) return best;
    }
    return best;
  };

  if (!opts.preferJpeg && webpSupported) {
    const webp = tryLadder('image/webp', _WEBP_QUALITIES);
    if (webp && webp.bytes <= maxBytes) return webp;
    // WebP couldn't fit even at lowest q — try JPEG as fallback.
    const jpeg = tryLadder('image/jpeg', _JPEG_QUALITIES);
    if (jpeg && jpeg.bytes <= maxBytes) return jpeg;
    // Both ladders over budget — return whichever was smaller.
    return jpeg && webp && jpeg.bytes < webp.bytes ? jpeg : (webp || jpeg);
  }
  // No WebP support (or explicit JPEG preference) — go straight to JPEG.
  return tryLadder('image/jpeg', _JPEG_QUALITIES);
}
